import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Select, Space, message, Modal, Statistic, Row, Col, Empty,
} from 'antd';
import {
  ReloadOutlined, DeleteOutlined, TeamOutlined, CheckCircleOutlined,
  ExportOutlined, MailOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { tenderAPI, loiAPI } from '../services/api';

const { Option } = Select;

function SeminarAdminPage() {
  const { t, i18n } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loiAPI.getCampaigns().then((res) => {
      const list = res.data || res;
      setCampaigns(Array.isArray(list) ? list : []);
      if (Array.isArray(list) && list.length > 0) {
        const saved = localStorage.getItem('spo_default_campaign_id');
        const savedId = saved ? Number(saved) : null;
        const match = list.find((c) => c.id === savedId);
        setSelectedCampaign(match ? match.id : list[0].id);
      }
    }).catch(() => message.error(t('seminar.loadCampaignsFailed')));
  }, []);

  const loadRegistrations = useCallback(async () => {
    if (!selectedCampaign) return;
    setLoading(true);
    try {
      const res = await tenderAPI.getSeminarRegistrations(selectedCampaign);
      const data = res.data || res;
      setRegistrations(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(t('seminar.loadRegFailed') + ': ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  const handleDelete = (regId, companyName) => {
    Modal.confirm({
      title: t('seminar.confirmDelete'),
      content: t('seminar.confirmDeleteMsg', { name: companyName }),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await tenderAPI.deleteSeminarRegistration(regId);
          message.success(t('seminar.deleted'));
          loadRegistrations();
        } catch (err) {
          message.error(t('seminar.deleteFailed') + ': ' + (err.message || ''));
        }
      },
    });
  };

  const handleExportCSV = () => {
    if (registrations.length === 0) {
      message.warning(t('seminar.noExportData'));
      return;
    }
    const headers = [
      t('seminar.csvHeaders.company'), t('seminar.csvHeaders.contact'),
      t('seminar.csvHeaders.email'), t('seminar.csvHeaders.jobTitle'),
      t('seminar.csvHeaders.phone'), t('seminar.csvHeaders.regTime'),
    ];
    const rows = registrations.map((r) => [
      r.company_name || '',
      r.contact_person || '',
      r.contact_email || '',
      r.job_title || '',
      r.phone || '',
      r.registered_at ? new Date(r.registered_at).toLocaleString(i18n.language) : '',
    ]);
    const bom = '\uFEFF';
    const csv = bom + [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seminar_registrations_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(t('seminar.csvExported'));
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: t('seminar.companyName'),
      dataIndex: 'company_name',
      key: 'company_name',
      ellipsis: true,
    },
    {
      title: t('seminar.contactPerson'),
      dataIndex: 'contact_person',
      key: 'contact_person',
    },
    {
      title: 'E-mail',
      dataIndex: 'contact_email',
      key: 'contact_email',
      ellipsis: true,
      render: (email) => email ? <a href={`mailto:${email}`}>{email}</a> : '-',
    },
    {
      title: t('seminar.jobTitle'),
      dataIndex: 'job_title',
      key: 'job_title',
    },
    {
      title: t('seminar.phone'),
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: t('seminar.seminarCode'),
      dataIndex: 'seminar_code',
      key: 'seminar_code',
      render: (code) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: t('seminar.regTime'),
      dataIndex: 'registered_at',
      key: 'registered_at',
      width: 180,
      render: (val) => val ? new Date(val).toLocaleString(i18n.language) : '-',
      sorter: (a, b) => {
        const ta = a.registered_at ? new Date(a.registered_at).getTime() : 0;
        const tb = b.registered_at ? new Date(b.registered_at).getTime() : 0;
        return ta - tb;
      },
      defaultSortOrder: 'descend',
    },
    {
      title: t('seminar.actions'),
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id, record.company_name)}
        >
          {t('common.delete')}
        </Button>
      ),
    },
  ];

  const selectedCampaignInfo = campaigns.find((c) => c.id === selectedCampaign);

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space>
              <TeamOutlined style={{ fontSize: 20, color: '#1890ff' }} />
              <span style={{ fontWeight: 600, fontSize: 16 }}>{t('seminar.pageTitle')}</span>
              <Select
                style={{ width: 360 }}
                placeholder={t('seminar.selectCampaign')}
                value={selectedCampaign}
                onChange={(val) => setSelectedCampaign(val)}
              >
                {campaigns.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.case_number ? `[${c.case_number}] ` : ''}{c.title}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadRegistrations}>
                {t('seminar.refresh')}
              </Button>
              <Button icon={<ExportOutlined />} onClick={handleExportCSV} disabled={registrations.length === 0}>
                {t('seminar.exportCsv')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}>
          <Card>
            <Statistic
              title={t('seminar.totalRegs')}
              value={registrations.length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={8}>
          <Card>
            <Statistic
              title={t('seminar.caseNumber')}
              value={selectedCampaignInfo?.case_number || '-'}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={8}>
          <Card>
            <Statistic
              title={t('seminar.companyCount')}
              value={new Set(registrations.map((r) => r.company_name)).size}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card title={t('seminar.regList')}>
        <Table
          columns={columns}
          dataSource={registrations}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => t('seminar.paginationTotal', { total }) }}
          locale={{ emptyText: <Empty description={t('seminar.noData')} /> }}
        />
      </Card>
    </div>
  );
}

export default SeminarAdminPage;
